import { useRef } from 'react';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import styled, { css } from 'styled-components';
import { DateAndGranularityPicker as _DateAndGranularityPicker } from '@clatter/ui';
import { CircularLoader } from '../../../shared';
import { DropdownSelect } from '../../UI';
import { SlidesThemeSelector } from '../../../pages/Presentations/components/SlidesThemeSelector';

const DateAndGranularityPickerAny = _DateAndGranularityPicker as any;

export const GROUP_BY_OPTIONS = {
  NONE: 'none',
  PPTX: 'pptx',
} as const;

export const SORT_DIRECTION_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
} as const;

export const SORT_BY_OPTIONS = {
  MODIFIED: 'modified',
  CREATED: 'created',
  NAME: 'name',
} as const;

export type GroupByOption = (typeof GROUP_BY_OPTIONS)[keyof typeof GROUP_BY_OPTIONS];
export type SortDirection = (typeof SORT_DIRECTION_OPTIONS)[keyof typeof SORT_DIRECTION_OPTIONS];
export type SortByOption = (typeof SORT_BY_OPTIONS)[keyof typeof SORT_BY_OPTIONS];

export interface DateRange {
  startDate: number;
  endDate: number;
}

export interface SlidesViewFilters {
  dateRange: DateRange;
  groupBy: GroupByOption;
  sortDirection: SortDirection;
  sortBy: SortByOption;
}

interface FolderManagerSlidesViewFiltersProps {
  filters: SlidesViewFilters;
  isFiltering?: boolean;
  viewStateKey?: string;
  showThemePicker?: boolean;
  inline?: boolean;
  compact?: boolean;
  onChange: (filters: SlidesViewFilters) => void;
}

const StyledFiltersContainer = styled.div<{ $inline?: boolean }>`
  container-type: inline-size;

  ${({ $inline }) =>
    $inline &&
    css`
      flex: 1 1 auto;
      min-width: 0;
    `}
`;

const StyledFilters = styled.div<{ $compact?: boolean; $inline?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: ${({ theme, $compact }) => ($compact ? theme.spacing.xSmall : theme.spacing.small)};
  padding: ${({ theme }) => theme.spacing.small} 0;

  font-size: ${({ theme, $compact }) => ($compact ? theme.fontSize.xs : theme.fontSize.default)};

  label {
    color: ${({ theme }) => theme.palette.gray};
    white-space: nowrap;
    font-size: inherit;
    flex-shrink: 0;
    min-width: 9ch;
  }

  .dropdown-select {
    min-width: ${({ $compact }) => ($compact ? '70px' : '120px')};
    ${({ $compact }) => $compact && css`flex: 1 1 0;`}
  }

  .filters-start {
    min-width: 0;

    > div:first-child {
      flex-wrap: wrap;
      row-gap: ${({ theme }) => theme.spacing.xSmall};

      > .body2:first-child {
        flex-shrink: 0;
      }
    }

    .form-control {
      padding-bottom: 0;
    }

    ${({ $compact, theme }) =>
      $compact &&
      css`
        .body2 {
          font-size: ${theme.fontSize.xs};
        }

        .react-select__control {
          min-height: 28px;
          height: 28px;
        }

        .react-select__value-container {
          height: 26px;
          padding: 0 6px;
        }

        .react-select__indicators {
          height: 26px;
        }

        .react-select__single-value,
        .react-select__placeholder,
        .react-select__input-container {
          font-size: ${theme.fontSize.xs};
        }
      `}

    ${({ $inline, theme }) =>
      $inline &&
      css`
        .body2 {
          margin-bottom: 0;
        }

        > div {
          column-gap: ${theme.spacing.default};

          > div {
            min-width: 0;
          }
        }

        .react-select__container {
          min-width: 0;
        }
      `}
  }

  .filters-theme {
    justify-self: center;
    width: 250px;
    flex-shrink: 0;
  }

  .filters-sort {
    justify-self: end;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme, $compact }) => ($compact ? theme.spacing.xSmall : theme.spacing.small)};

    .filter-group {
      display: flex;
      align-items: center;
      gap: ${({ theme, $compact }) => ($compact ? theme.spacing.xSmall : theme.spacing.small)};
    }

    ${({ $inline, theme }) =>
      $inline &&
      css`
        margin-left: ${theme.spacing.default};
      `}
  }

  ${({ $inline }) =>
    $inline
      ? css`
          @container (max-width: 900px) {
            grid-template-columns: 1fr;

            .filters-start,
            .filters-sort {
              justify-self: stretch;
            }

            .filters-sort {
              margin-left: 0;
              display: grid;
              grid-template-columns: 1fr 1fr;
              column-gap: ${({ theme }) => theme.spacing.medium};
            }

            .filters-sort > button {
              grid-column: 1 / -1;
              justify-self: end;
            }

            .filters-start > div:first-child > div:has([data-test-id="selected-date-range-text"]) {
              margin-left: auto;
            }

            .filters-start > div:first-child > div:nth-child(2) {
              flex: 1;
              max-width: 220px;
            }

            .dropdown-select {
              flex: 1;
              min-width: 0;
              max-width: 220px;
            }
          }
        `
      : css`
          @container (max-width: 1200px) {
            grid-template-columns: 1fr 1fr;

            &:has(.filters-theme) .filters-start {
              grid-column: 1 / -1;
            }

            .filters-theme {
              justify-self: start;
            }

            .filters-sort {
              justify-self: end;
            }
          }

          @container (max-width: 700px) {
            grid-template-columns: 1fr;

            .filters-start,
            .filters-theme,
            .filters-sort {
              justify-self: stretch;
            }

            .filters-sort {
              display: grid;
              grid-template-columns: 1fr 1fr;
              column-gap: ${({ theme }) => theme.spacing.medium};
            }

            .filters-sort > button {
              grid-column: 1 / -1;
              justify-self: end;
            }

            .filters-start > div:first-child > div:has([data-test-id="selected-date-range-text"]) {
              margin-left: auto;
            }

            .dropdown-select {
              flex: 1;
              min-width: 0;
            }
          }
        `}
`;

const SortDirectionButton = styled.button<{ $isAscending: boolean; $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.palette.border};
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  padding: ${({ $compact }) => ($compact ? '4px 6px' : '6px 8px')};
  font-size: ${({ $compact }) => ($compact ? '12px' : '14px')};
  line-height: 1;
  color: ${({ theme }) => theme.palette.gray};
  transition: transform 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.hoverBackgroundColor};
  }

  span {
    display: inline-block;
    transition: transform 0.2s ease;
    transform: ${({ $isAscending }) => ($isAscending ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const getDateRangeFromFilters = (filters: SlidesViewFilters): DateRange | null => {
  return filters.dateRange;
};

const isDateInRange = (dateStr: string | null | undefined, dateRange: DateRange): boolean => {
  if (!dateStr) return false;

  const itemDate = new Date(dateStr).getTime();
  const afterStart = !dateRange.startDate || itemDate >= dateRange.startDate;
  const beforeEnd = !dateRange.endDate || itemDate <= dateRange.endDate;
  return afterStart && beforeEnd;
};

const sortSlides = <T extends { updatedAt?: string | null; createdAt?: string; displayName?: string; name?: string }>(
  items: T[],
  filters: SlidesViewFilters,
): T[] => {
  const isDescending = filters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST;

  return [...items].sort((a, b) => {
    let comparison = 0;

    if (filters.sortBy === SORT_BY_OPTIONS.NAME) {
      comparison = (a.displayName || a.name || '').toLowerCase().localeCompare((b.displayName || b.name || '').toLowerCase());
    } else {
      const field = filters.sortBy === SORT_BY_OPTIONS.CREATED ? 'createdAt' : 'updatedAt';
      const dateA = a[field] ? new Date(a[field] as string).getTime() : 0;
      const dateB = b[field] ? new Date(b[field] as string).getTime() : 0;
      comparison = dateA - dateB;
    }

    return isDescending ? -comparison : comparison;
  });
};

export const filterAndSortSlides = <T extends { updatedAt?: string | null; createdAt?: string; displayName?: string; name?: string }>(
  items: T[],
  filters: SlidesViewFilters,
): T[] => {
  const dateRange = getDateRangeFromFilters(filters);

  let result = items;

  if (dateRange) {
    result = result.filter((item) => {
      const dateStr = item.updatedAt || item.createdAt;
      return !dateStr || isDateInRange(dateStr, dateRange);
    });
  }

  return sortSlides(result, filters);
};

// a deck's own updatedAt only reflects version-level changes, not edits to individual slides inside
// it - so a deck matches the date filter if it OR any of its slides falls within the range
export const filterAndSortDecks = <
  T extends { updatedAt?: string | null; createdAt?: string; displayName?: string; name?: string; slides?: any[] },
>(
  decks: T[],
  filters: SlidesViewFilters,
): T[] => {
  const dateRange = getDateRangeFromFilters(filters);

  let result = decks;

  if (dateRange) {
    result = result.filter((deck) => {
      const candidateDates = [
        deck.updatedAt || deck.createdAt,
        ...(deck.slides || []).map((slide: any) => slide.itemData?.UpdatedAt),
      ].filter(Boolean) as string[];

      if (!candidateDates.length) return true;

      return candidateDates.some((dateStr) => isDateInRange(dateStr, dateRange));
    });
  }

  return sortSlides(result, filters);
};

const getLast365DaysRange = (): DateRange => ({
  startDate: startOfDay(subDays(new Date(), 365)).getTime(),
  endDate: endOfDay(new Date()).getTime(),
});

export const DEFAULT_SLIDES_FILTERS: SlidesViewFilters = {
  dateRange: getLast365DaysRange(),
  groupBy: GROUP_BY_OPTIONS.NONE,
  sortDirection: SORT_DIRECTION_OPTIONS.NEWEST,
  sortBy: SORT_BY_OPTIONS.MODIFIED,
};

export const isDefaultFilters = (filters: SlidesViewFilters, defaultFilters: SlidesViewFilters = DEFAULT_SLIDES_FILTERS) =>
  filters.dateRange?.endDate === defaultFilters.dateRange?.endDate &&
  filters.dateRange?.startDate === defaultFilters.dateRange?.startDate &&
  filters.groupBy === defaultFilters.groupBy &&
  filters.sortBy === defaultFilters.sortBy &&
  filters.sortDirection === defaultFilters.sortDirection;

const groupByOptions: IDropdownSelectOptionItem[] = [
  { value: GROUP_BY_OPTIONS.NONE, label: 'None' },
  { value: GROUP_BY_OPTIONS.PPTX, label: 'PPTX' },
];

const sortByOptions: IDropdownSelectOptionItem[] = [
  { value: SORT_BY_OPTIONS.MODIFIED, label: 'Modified' },
  { value: SORT_BY_OPTIONS.CREATED, label: 'Created' },
  { value: SORT_BY_OPTIONS.NAME, label: 'Name' },
];

const compactDropdownStyles = {
  control: (provided: any) => ({
    ...provided,
    minHeight: '28px',
    height: '28px',
    fontSize: '11px',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '0 6px',
    height: '26px',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '26px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: '0 4px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '11px',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: 0,
    padding: 0,
    fontSize: '11px',
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '11px',
  }),
};

export const FolderManagerSlidesViewFilters = ({
  filters,
  isFiltering,
  viewStateKey,
  showThemePicker = true,
  inline = false,
  compact = false,
  onChange,
}: FolderManagerSlidesViewFiltersProps) => {
  const isInitialDateRangeChange = useRef(true);

  const handleDateRangeChange = ({ dateRange }: any) => {
    // skip the picker's mount-time onChange, which fires its default range
    // before the user has interacted with it. Deferred via microtask since
    // React StrictMode runs mount effects twice synchronously.
    if (isInitialDateRangeChange.current) {
      queueMicrotask(() => {
        isInitialDateRangeChange.current = false;
      });
      return;
    }

    if (!dateRange?.range?.startDate || !dateRange?.range?.endDate) {
      onChange({ ...filters, dateRange: getLast365DaysRange() });
      return;
    }

    onChange({
      ...filters,
      dateRange: {
        startDate: new Date(dateRange.range.startDate).getTime(),
        endDate: new Date(dateRange.range.endDate).getTime(),
      },
    });
  };

  return (
    <StyledFiltersContainer $inline={inline}>
    <StyledFilters $compact={compact} $inline={inline}>
      <div className="filters-start">
        <DateAndGranularityPickerAny
          label="Modified:"
          onChange={handleDateRangeChange}
          showGranularity={false}
          defaultDateRangeValue="last-365-days"
        />

        {isFiltering && <CircularLoader size={20} testId="filters-loader" />}
      </div>

      {showThemePicker && viewStateKey && (
        <div className="filters-theme">
          <SlidesThemeSelector viewStateKey={viewStateKey} />
        </div>
      )}

      <div className="filters-sort">
          <div className="filter-group">
            <label>Group by:</label>
            <div className="dropdown-select">
              <DropdownSelect
                options={groupByOptions}
                value={groupByOptions.find((o) => o.value === filters.groupBy) ?? null}
                onChange={(option: IDropdownSelectOptionItem) =>
                  onChange({ ...filters, groupBy: (option?.value ?? GROUP_BY_OPTIONS.NONE) as GroupByOption })
                }
                isSearchable={false}
                styles={compact ? compactDropdownStyles : undefined}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Sort by:</label>
            <div className="dropdown-select">
              <DropdownSelect
                options={sortByOptions}
                value={sortByOptions.find((o) => o.value === filters.sortBy) ?? null}
                onChange={(option: IDropdownSelectOptionItem) =>
                  onChange({ ...filters, sortBy: (option?.value ?? SORT_BY_OPTIONS.MODIFIED) as SortByOption })
                }
                isSearchable={false}
                styles={compact ? compactDropdownStyles : undefined}
              />
            </div>
            <SortDirectionButton
              $compact={compact}
              $isAscending={filters.sortDirection === SORT_DIRECTION_OPTIONS.OLDEST}
              onClick={() =>
                onChange({
                  ...filters,
                  sortDirection:
                    filters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST
                      ? SORT_DIRECTION_OPTIONS.OLDEST
                      : SORT_DIRECTION_OPTIONS.NEWEST,
                })
              }
              title={filters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST ? 'Descending' : 'Ascending'}
            >
              <span>&#x2193;</span>
            </SortDirectionButton>
          </div>
        </div>
    </StyledFilters>
    </StyledFiltersContainer>
  );
};
